import { FC, useCallback, useEffect, useState } from "react";
import { Button, Dialog, Spinner, TextButton } from "@livingmap/hippo";
import cryptoRandomString from "crypto-random-string";
import { useTranslation } from "react-i18next";

import PageTitle from "@components/PageTitle/PageTitle";
import ResetSidebar from "@components/EditLinkSidebar/Modals/ResetSidebar";
import EditLinkSidebar from "@components/EditLinkSidebar/EditLinkSidebar";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useGetMapsQuery } from "@redux/services/mms";
import {
  useGetProjectLinksQuery,
  useCreateSingleLinkMutation,
  useLazyGetSingleLinkQuery,
  useUpdateSingleLinkMutation,
  linksApi,
} from "@redux/services/links";
import {
  Project,
  LinkUpdateRequest,
  ProjectLinkData,
} from "@redux/services/types";
import {
  setDefaultLanguage,
  setHasUnsavedChanges,
  setLinkScanCode,
  setMapID,
} from "@redux/slices/applicationSlice";
import {
  getProjectNames,
  formatErrors,
  displayToast,
  FormattedErrors,
} from "@utils";
import UnsavedChanges from "../FeaturesView/Modal/UnsavedChanges";
import styles from "./ShortLinksView.module.scss";
import ShortlinkPanel from "@components/ShortlinkPanel/ShortlinkPanel";
import FormControl from "@components/FormControl/FormControl";
import NavigationTopBar from "@components/NavigationTopBar/NavigationTopBar";
import ContentPanel from "@components/ContentPanel/ContentPanel";
import { useKeycloak } from "@react-keycloak/web";
import { ParsedToken } from "screens/FeaturesView/FeaturesView";

interface Props {
  onSignOut: () => void;
}

const ShortLinksView: FC<Props> = ({ onSignOut }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const [createSingleLink, { error: createSingleLinkError }] =
    useCreateSingleLinkMutation();
  const [updateSingleLink, { error: updateSingleLinkError }] =
    useUpdateSingleLinkMutation();
  const { keycloak } = useKeycloak();
  const token = keycloak.tokenParsed as ParsedToken;

  const [
    getSingleLink,
    {
      data: singleLink,
      isFetching: isFetchingSingleLink,
      isSuccess: isGetSingleLinkSuccess,
    },
  ] = useLazyGetSingleLinkQuery();

  const [isUnsavedChangesModalActive, setUnsavedChangesModalActive] =
    useState(false);
  const [localLinkScanCode, setLocalLinkScanCode] = useState<string | null>(
    null,
  );
  const [newLinkData, setNewLinkData] =
    useState<Partial<ProjectLinkData> | null>(null);

  const [formattedErrors, setFormattedErrors] =
    useState<FormattedErrors | null>(null);

  const { mapID, linkScanCode, hasUnsavedChanges, defaultLanguage } =
    useAppSelector((state) => state.application);

  const { data, isLoading } = useGetMapsQuery();
  const { data: projectLinks, isFetching: isFetchingLinks } =
    useGetProjectLinksQuery(mapID, {
      skip: !mapID,
    });

  useEffect(() => {
    if (!isFetchingLinks) {
      dispatch(setHasUnsavedChanges(false));
    }
  }, [isFetchingLinks, dispatch]);

  const projectNames = getProjectNames(data);
  const activeProjectName = projectNames?.find(
    (project) => project.id === mapID,
  );
  const activeProject = data?.data.find((project) => project.id === mapID);

  const handleProjectSelect = (project: Project) => {
    dispatch(setMapID(project.id));
    dispatch(setDefaultLanguage(project.defaultLanguage));
    dispatch(setLinkScanCode(null));
  };

  const handleShortlinkEditClick = useCallback(
    (link: ProjectLinkData) => {
      if (hasUnsavedChanges) {
        setUnsavedChangesModalActive(true);
        setLocalLinkScanCode(link.scan_code);
        return;
      }

      setLocalLinkScanCode(null);
      setNewLinkData(null);
      getSingleLink(link.scan_code);
      dispatch(setLinkScanCode(link.scan_code));
    },
    [dispatch, hasUnsavedChanges, getSingleLink],
  );

  const mappedDataToShortlinkPanels = projectLinks?.data.map((link) => {
    const hasBeenModified = link.created_at !== link.last_modified;
    return (
      <ShortlinkPanel
        dataQA={`shorlink-${link.scan_code}`}
        createdAt={link.created_at}
        modifiedAt={hasBeenModified ? link.last_modified : undefined}
        name={link.name}
        onEdit={() => handleShortlinkEditClick(link)}
        qrCode={link.qr_code}
        shortlinkUrl={`${link.shortlink_url}/${link.scan_code}`}
        key={link.scan_code}
      />
    );
  });

  const handleCreateNewLink = () => {
    if (!mapID) return;

    dispatch(linksApi.util.resetApiState());

    const scanCode = cryptoRandomString({
      length: 6,
      type: "alphanumeric",
    }).toLowerCase();

    const shortlinkUrl =
      process.env.REACT_APP_ENVIRONMENT === "prod"
        ? "https://livingmap.link"
        : process.env.REACT_APP_SHORTLINK_API_URL;

    const newLink: Partial<ProjectLinkData> = {
      project: mapID,
      name: "",
      real_url: "",
      shortlink_url: shortlinkUrl,
      scan_code: scanCode,
    };

    setNewLinkData(newLink);
  };

  const handleDataChanged = (dataChanged: boolean) => {
    dispatch(setHasUnsavedChanges(dataChanged));
  };

  const handleEditLinkSave = async (data: Partial<ProjectLinkData>) => {
    if (newLinkData) {
      // New shortlink path
      await createSingleLink(data as ProjectLinkData);
      setFormattedErrors(null);
      getSingleLink(newLinkData.scan_code!);
    } else {
      // Edit shortlink path
      const updateRequestBody: LinkUpdateRequest = {
        name: data.name,
        shortlink_url: data.shortlink_url,
        metadata: data.metadata,
        scan_code: data.scan_code,
        real_url: data.real_url,
      };
      updateSingleLink(updateRequestBody);
      dispatch(setHasUnsavedChanges(false));
      setFormattedErrors(null);
    }
  };

  useEffect(() => {
    if (isGetSingleLinkSuccess) {
      setNewLinkData(null);
      dispatch(setHasUnsavedChanges(false));
    }
  }, [dispatch, isGetSingleLinkSuccess]);

  useEffect(() => {
    if (!createSingleLinkError && !updateSingleLinkError) return;

    if (createSingleLinkError) {
      displayToast("error", t("views.links.errors.create"));
    } else {
      displayToast("error", t("views.links.errors.update"));
    }

    setFormattedErrors(
      formatErrors(createSingleLinkError || updateSingleLinkError),
    );
  }, [createSingleLinkError, t, updateSingleLinkError]);

  const handleSidebarClose = () => {
    setNewLinkData(null);
    setFormattedErrors(null);
    dispatch(setLinkScanCode(null));
    dispatch(setHasUnsavedChanges(false));
    dispatch(linksApi.util.resetApiState());
  };

  const handleConfirmUnsavedChanges = () => {
    if (!localLinkScanCode) return;

    dispatch(setLinkScanCode(localLinkScanCode));
    setUnsavedChangesModalActive(false);
    setNewLinkData(null);
    dispatch(linksApi.util.resetApiState());
    getSingleLink(localLinkScanCode);
  };

  useEffect(() => {
    if (singleLink) {
      dispatch(setLinkScanCode(singleLink.scan_code));
    }
  }, [singleLink, dispatch]);

  const [isResetSidebarModalActive, setResetSidebarModalActive] =
    useState(false);

  const resetSidebarModal = () => (
    <Dialog
      dataQA="reset-sidebar-dialog"
      isOpen={isResetSidebarModalActive}
      onClose={() => setResetSidebarModalActive(false)}
    >
      <ResetSidebar
        resetModal={() => setResetSidebarModalActive(false)}
        onResetSidebar={() => {
          handleCreateNewLink();
          setResetSidebarModalActive(false);
        }}
      />
    </Dialog>
  );

  return isLoading ||
    !mapID ||
    !activeProject ||
    !activeProjectName ||
    !projectNames ? (
    <div className={styles.loaderContainer} data-qa="shortlinks-view-loading">
      <Spinner dataQA="loading-spinner" size={36} />
    </div>
  ) : (
    <div className={styles.container} data-qa="shortlinks-view">
      <NavigationTopBar
        dataQA="features-view-navigation-top-bar"
        activeLanguage={defaultLanguage}
        languages={activeProject.languages.available}
        activeProject={activeProjectName}
        projects={projectNames}
        onProjectChange={handleProjectSelect}
        onLanguageChange={(lang) => {
          dispatch(setDefaultLanguage(lang.code));
          i18n.changeLanguage(lang.code);
        }}
        userProfile={token}
        userMenuItems={[
          {
            name: t("header.menu_items.sign_out"),
            icon: "logout",
            onClick: onSignOut,
            type: "primary",
          },
        ]}
      />
      <ContentPanel dataQA="shortlinks-view-content-panel">
        <PageTitle
          dataQA="link-management-page-title"
          heading={t("views.links.title")}
          subheading={t("views.links.description")}
          customButton={{
            text: t("views.links.create_link"),
            onClick: () =>
              hasUnsavedChanges
                ? setResetSidebarModalActive(true)
                : handleCreateNewLink(),
          }}
        />
        <div className={styles.outletContainer}>
          <div className={styles.contentContainer}>
            {projectLinks?.data.length === 0 ? (
              <div className={styles.noShortlinksContainer}>
                <span>{t("views.links.create_first_link_header")}</span>
                <Button
                  dataQA="create-first-shortlink-button"
                  onClick={handleCreateNewLink}
                  icon="plus"
                  type="primary"
                  size="regular"
                  style="filled"
                  text={t("views.links.create_first_link_button")}
                />
              </div>
            ) : (
              <div className={styles.shortlinkPanelsContainer}>
                {mappedDataToShortlinkPanels}
              </div>
            )}
            {linkScanCode ? (
              <EditLinkSidebar
                errors={formattedErrors}
                isLoading={isFetchingSingleLink}
                linkData={singleLink || null}
                onDataChanged={handleDataChanged}
                onEditLinkSave={handleEditLinkSave}
                onSidebarCloseClick={handleSidebarClose}
              />
            ) : null}
            <Dialog
              dataQA="unsaved-changes-dialog"
              isOpen={isUnsavedChangesModalActive}
              onClose={() => setUnsavedChangesModalActive(false)}
              footer={false}
            >
              <UnsavedChanges
                onCancel={() => setUnsavedChangesModalActive(false)}
                onConfirm={handleConfirmUnsavedChanges}
              />
            </Dialog>
            <Dialog
              dataQA="create-shortlink-dialog"
              isOpen={newLinkData !== null}
              onClose={() => setNewLinkData(null)}
              footer={false}
            >
              <div className={styles.createShortlinkDialog}>
                <div className={styles.createShortlinkHeader}>
                  <span>Create short link</span>
                </div>
                <div className={styles.createShortlinkBody}>
                  <div className={styles.formField}>
                    <span className={styles.fieldTitle}>Long URL</span>
                    <FormControl
                      dataQA="real-link-textarea"
                      type="text"
                      id="real_url"
                      name="real_url"
                      value={newLinkData?.real_url}
                      onChange={(e) =>
                        setNewLinkData({
                          ...newLinkData,
                          real_url: e.target.value,
                        })
                      }
                    />
                    {formattedErrors && formattedErrors["real_url"] && (
                      <ul>
                        {formattedErrors["real_url"].map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className={styles.formField}>
                    <span className={styles.fieldTitle}>Short URL</span>
                    <div className={styles.fieldInlineControl}>
                      <span>{newLinkData?.shortlink_url}/</span>
                      <FormControl
                        dataQA="real-link-textarea"
                        type="text"
                        id="real_url"
                        name="real_url"
                        value={newLinkData?.scan_code}
                        disabled
                      />
                    </div>
                  </div>
                  <div className={styles.formField}>
                    <span className={styles.fieldTitle}>Name this link</span>
                    <FormControl
                      dataQA="real-link-textarea"
                      type="text"
                      id="name"
                      name="name"
                      value={newLinkData?.name}
                      onChange={(e) =>
                        setNewLinkData({ ...newLinkData, name: e.target.value })
                      }
                    />
                    {formattedErrors && formattedErrors["name"] && (
                      <ul>
                        {formattedErrors["name"].map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className={styles.createShortlinkFooter}>
                  <TextButton
                    dataQA="create-shortlink-cancel-button"
                    onClick={handleSidebarClose}
                    type="primary"
                    size="regular"
                    text="Cancel"
                  />
                  <Button
                    dataQA="create-shortlink-confirm-button"
                    onClick={() => {
                      handleEditLinkSave(newLinkData!);
                    }}
                    type="primary"
                    size="regular"
                    style="filled"
                    text="Create short link"
                  />
                </div>
              </div>
            </Dialog>
            {resetSidebarModal()}
          </div>
        </div>
      </ContentPanel>
    </div>
  );
};

export default ShortLinksView;
